import { useState, useEffect, useMemo, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Bell, X, ChevronLeft, ChevronRight, Megaphone, Package } from "lucide-react";
import { useAnnouncementContext } from "../context/AnnouncementContext";
import { useProductContext } from "../context/ProductContext";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { useQuery } from "@tanstack/react-query";
import { getAnnouncements, type Announcement } from "../services/AnnouncementService";
import { getProductImageUrl } from "../utils/imageUtils";
import { isOnlineOnlyRetailer } from "../config/retailers";
import { ALERT_DAY_LABELS, alertDiscountPercent, alertSubject, alertTag, buildAlertFeed } from "../utils/alertFeed";
import { AnnouncementHero, AnnouncementRow, EmptyState, FeedRow, RowThumb, TabButton } from "./AnnouncementShared";
import { formatRelativeTime, percentLabel, type Lang } from "../utils/announcementText";

interface AnnouncementButtonProps {
  iconSize?: number;
  variant?: "light" | "dark" | "sidebar" | "sheet";
}

type TabType = "announcements" | "alerts";

const getBrowserLanguage = (): Lang => {
  const browserLang = navigator.language || (navigator as { userLanguage?: string }).userLanguage || "en";
  return browserLang.toLowerCase().startsWith("es") ? "es" : "en";
};

const formatMoney = (value?: number) => (value && value > 0 ? `$${value.toFixed(2)}` : "");

// Feed row: product photo, one-line name, then price (old price struck) · discount or tag · store · time.
function AlertItem({ alert, lang, onClick, onDismiss }: { alert: Announcement; lang: Lang; onClick: () => void; onDismiss?: () => void }) {
  const product = alert.alertProduct;
  const newPrice = formatMoney(product?.newPrice);
  const oldPrice = product?.oldPrice && product?.newPrice && product.oldPrice > product.newPrice ? formatMoney(product.oldPrice) : "";
  const pct = alertDiscountPercent(alert);
  const tag = alertTag(alert, lang);
  // Stores come as "60 Sw 12th Ave, Deerfield Beach, FL"; the city reads best in one line.
  const storeParts = (product?.store || "").split(",").map((part) => part.trim()).filter(Boolean);
  const store = storeParts.length >= 3 ? storeParts[storeParts.length - 2] : storeParts[0];
  const tail = [store, formatRelativeTime(alert.createdAt, lang)].filter(Boolean).join(" · ");
  const lead = [
    newPrice && <span key="p" className="font-semibold text-[#17122b] dark:text-white">{newPrice}</span>,
    oldPrice && <span key="o"> <span className="line-through">{oldPrice}</span></span>,
  ];
  const chips = [
    pct > 0 && <span key="d" className="font-semibold text-green-700 dark:text-green-400">{percentLabel(pct, lang)}</span>,
    tag && <span key="t" className="font-semibold text-[#6d28d9] dark:text-purple-300">{tag}</span>,
  ].filter(Boolean);
  const parts: ReactNode[] = [];
  if (newPrice) parts.push(<span key="lead">{lead}</span>);
  chips.forEach((chip) => parts.push(chip));
  if (tail) parts.push(<span key="tail">{tail}</span>);

  return (
    <FeedRow
      onClick={onClick}
      thumb={<RowThumb src={product?.imageUrl ? getProductImageUrl(product.imageUrl, "150") : undefined} icon={<Package className="h-5 w-5" />} />}
      title={alertSubject(alert)}
      meta={parts.map((part, i) => <span key={i}>{i > 0 ? " · " : ""}{part}</span>)}
      trailing={onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="flex h-8 w-8 items-center justify-center rounded-full text-[#8a849b] transition hover:bg-[#f3f1f7] hover:text-[#17122b] dark:text-dark-400 dark:hover:bg-dark-600 dark:hover:text-white"
          aria-label={lang === "es" ? "Quitar alerta" : "Dismiss alert"}
          title={lang === "es" ? "Quitar alerta" : "Dismiss alert"}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    />
  );
}

export default function AnnouncementButton({ iconSize = 24, variant = "light" }: AnnouncementButtonProps) {
  const { unseenCount, markAllSeen, allAlerts, unseenAlertCount, markAllAlertsSeen, dismissAlert, clearAllAlerts } = useAnnouncementContext();
  const { selectedProduct } = useProductContext();
  const { user } = useUserContext();
  const { currentLanguage: language } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("alerts");
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [selectedLang, setSelectedLang] = useState<Lang>(() => (language === "es" ? "es" : getBrowserLanguage()));

  const { data: announcements } = useQuery({
    queryKey: ["user-announcements"],
    queryFn: () => getAnnouncements(20),
    enabled: isOpen,
  });

  const alertFeed = useMemo(() => buildAlertFeed(allAlerts), [allAlerts]);

  useEffect(() => {
    setSelectedLang(language === "es" ? "es" : "en");
  }, [language]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const handleOpen = () => {
    setIsOpen(true);
    markAllAlertsSeen();
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedAnnouncement(null);
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSelectedAnnouncement(null);
    if (tab === "alerts") markAllAlertsSeen();
    if (tab === "announcements" && unseenCount > 0) markAllSeen();
  };

  const storeNameToRetailerKey = (store?: string): string | null => {
    if (!store) return null;
    const map: Record<string, string> = {
      "walmart": "walmart", "home depot": "home_depot", "best buy": "bestbuy",
      "dollar general": "dollar_general", "target": "target", "woot": "woot",
    };
    return map[store.toLowerCase()] || null;
  };

  const getFirstStoreForRetailer = (retailerKey: string): string | null => {
    if (!user) return null;
    switch (retailerKey) {
      case "walmart": return user.storeIds?.[0] || null;
      case "home_depot": return user.homeDepotStoreIds?.[0] || null;
      case "dollar_general": return user.dollarGeneralStoreIds?.[0] || null;
      case "bestbuy": return user.bestbuyStoreIds?.[0] || null;
      case "target": return user.targetStoreIds?.[0] || null;
      case "lowes": return user.lowesStoreIds?.[0] || null;
      default: return user.storeIds?.[0] || null;
    }
  };

  const handleAlertClick = (alert: Announcement) => {
    setIsOpen(false);
    markAllSeen();
    const product = alert.alertProduct;
    if (!product?.upc) {
      window.location.href = `/${selectedProduct}/tracking`;
      return;
    }
    const retailer = (product.retailer || storeNameToRetailerKey(product.store) || selectedProduct).replace(/_online$/, "");
    const onlineOnly = isOnlineOnlyRetailer(retailer);
    const storeId = onlineOnly ? "online" : (product.storeId || getFirstStoreForRetailer(retailer));
    if (storeId) {
      window.location.href = `/${retailer}/product/${product.upc}?storeId=${storeId}`;
    } else {
      window.location.href = `/${retailer}/product/${product.upc}`;
    }
  };


  const iconColor = variant === "light" ? "text-white/80 hover:text-white" : "text-gray-600 hover:text-gray-900";
  const hasAnySpanish = announcements?.announcements.some(a => a.titleEs || a.bodyEs);
  const totalUnseen = unseenCount + unseenAlertCount;

  const es = selectedLang === "es";
  const iconButton = "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[#625c75] transition hover:bg-[#f3f1f7] hover:text-[#17122b] dark:text-dark-300 dark:hover:bg-dark-700 dark:hover:text-white";

  const renderModal = () => isOpen && createPortal(
    <div className="fixed inset-0 z-[10001] flex items-end justify-center bg-[#0d0a17]/50 sm:items-center sm:p-4 sheet-backdrop-in" onClick={handleClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="notifications-title"
        className="flex h-[75vh] w-full flex-col overflow-hidden rounded-t-[28px] bg-white text-[#17122b] shadow-[0_20px_60px_-12px_rgba(13,10,23,0.35)] sheet-panel-in-md sm:h-auto sm:max-h-[82vh] sm:max-w-[440px] sm:rounded-[28px] dark:bg-dark-800 dark:text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="relative flex-shrink-0 border-b border-[#efedf3] px-5 pt-5 sm:pt-4 dark:border-dark-700">
          <div className="absolute top-2 left-1/2 h-1 w-9 -translate-x-1/2 rounded-full bg-[#e4e0ec] sm:hidden dark:bg-dark-600" />
          <div className="flex h-9 items-center gap-2">
            {selectedAnnouncement && (
              <button type="button" onClick={() => setSelectedAnnouncement(null)} aria-label={es ? "Atrás" : "Back"} className={`${iconButton} -ml-2`}>
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            <h2 id="notifications-title" className="m-0 flex-1 truncate text-[17px] font-semibold tracking-[-0.01em]">
              {selectedAnnouncement ? (es ? "Anuncio" : "Announcement") : (es ? "Notificaciones" : "Notifications")}
            </h2>
            {(hasAnySpanish || activeTab === "alerts") && !selectedAnnouncement && (
              <div className="flex items-center">
                {(["en", "es"] as const).map((l, i) => (
                  <span key={l} className="flex items-center">
                    {i > 0 && <span className="text-xs text-[#d6d2de] dark:text-dark-600" aria-hidden="true">/</span>}
                    <button
                      type="button"
                      aria-pressed={selectedLang === l}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedLang(l); }}
                      className={`h-7 rounded-md px-1.5 text-xs font-semibold transition ${selectedLang === l ? "text-[#17122b] dark:text-white" : "text-[#b0abbd] hover:text-[#625c75] dark:text-dark-500 dark:hover:text-dark-300"}`}
                    >
                      {l.toUpperCase()}
                    </button>
                  </span>
                ))}
              </div>
            )}
            <button type="button" onClick={handleClose} aria-label={es ? "Cerrar" : "Close"} className={`${iconButton} -mr-2`}>
              <X className="h-5 w-5" />
            </button>
          </div>

          {selectedAnnouncement ? (
            <div className="h-3" />
          ) : (
            <div role="tablist" className="mt-2 flex gap-6">
              <TabButton active={activeTab === "alerts"} onClick={() => handleTabChange("alerts")} label={es ? "Alertas" : "Alerts"} count={unseenAlertCount} />
              <TabButton active={activeTab === "announcements"} onClick={() => handleTabChange("announcements")} label={es ? "Novedades" : "Updates"} count={unseenCount} />
            </div>
          )}
        </header>

        {/* Content: re-mounts per tab so it fades/slides in on each switch */}
        <div key={selectedAnnouncement ? "detail" : activeTab} className={`flex-1 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)] ${activeTab === "alerts" ? "notif-tab-in-left" : "notif-tab-in-right"}`}>
          {selectedAnnouncement ? (
            <AnnouncementHero key={selectedAnnouncement.createdAt} announcement={selectedAnnouncement} lang={selectedLang} />
          ) : activeTab === "announcements" ? (
            !announcements?.announcements.length ? (
              <EmptyState icon={<Megaphone className="h-5 w-5" />} title={es ? "No hay anuncios aún" : "No announcements yet"} />
            ) : (
              <div className="py-2">
                {announcements.announcements.map((announcement) => (
                  <AnnouncementRow key={announcement.createdAt} announcement={announcement} lang={selectedLang} onClick={() => setSelectedAnnouncement(announcement)} />
                ))}
              </div>
            )
          ) : alertFeed.length > 0 ? (
            <div className="pb-3">
              <div className="flex justify-end px-5 pt-2">
                <button type="button" onClick={clearAllAlerts} className="rounded-md px-1.5 py-1 text-[13px] font-semibold text-[#8a849b] transition hover:text-red-600 dark:text-dark-400 dark:hover:text-red-400">
                  {es ? "Borrar todo" : "Clear all"}
                </button>
              </div>
              {alertFeed.map((row, index) => row.type === "day" ? (
                <h3 key={`day-${row.day}`} className="m-0 px-5 pt-3 pb-1 text-[13px] font-semibold text-[#8a849b] dark:text-dark-400">{ALERT_DAY_LABELS[row.day][selectedLang]}</h3>
              ) : (
                <AlertItem
                  key={`${row.alert.createdAt}-${index}`}
                  alert={row.alert}
                  lang={selectedLang}
                  onClick={() => handleAlertClick(row.alert)}
                  onDismiss={() => dismissAlert(row.alert.createdAt)}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<Bell className="h-5 w-5" />}
              title={es ? "Sin alertas de productos" : "No product alerts yet"}
              hint={es ? "Rastrea productos para recibir alertas de bajadas de precio" : "Track products to get notified of price drops"}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );

  // Sidebar variant
  if (variant === "sheet") {
    return (
      <>
        <button
          type="button"
          onClick={handleOpen}
          className="flex min-h-[54px] w-full items-center gap-3 border-b border-gray-100 text-left dark:border-dark-700"
          aria-label="Notifications"
        >
          <span className="relative flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-gray-100 text-primary-700 dark:bg-white/[0.06] dark:text-primary-300">
            <Bell size={19} />
            {totalUnseen > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-0.5 text-[10px] font-bold text-white">
                {totalUnseen > 9 ? "9+" : totalUnseen}
              </span>
            )}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] font-semibold text-gray-900 dark:text-white">Notifications</span>
            {totalUnseen > 0 && <span className="block truncate text-xs text-gray-500 dark:text-dark-300">{totalUnseen} unread</span>}
          </span>
          <ChevronRight size={16} className="flex-shrink-0 text-gray-400" />
        </button>
        {renderModal()}
      </>
    );
  }

  if (variant === "sidebar") {
    return (
      <>
        <button
          onClick={handleOpen}
          className="group relative flex items-center gap-3 w-full px-3 py-2.5 md:py-2 rounded-lg text-sm font-medium transition-[colors,background-color,box-shadow,transform] duration-300 text-primary-200 hover:text-white hover:bg-primary-800/40 hover:shadow-lg hover:shadow-primary-900/20"
        >
          <div className="relative transition-all duration-300 group-hover:text-primary-300 group-hover:scale-110">
            <Bell size={iconSize} />
            {totalUnseen > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5">
                {totalUnseen > 9 ? "9+" : totalUnseen}
              </span>
            )}
          </div>
          <span className="transition-all duration-300 group-hover:translate-x-0.5 text-sm">Notifications</span>
          <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-primary-600/0 to-primary-500/0 group-hover:from-primary-600/20 group-hover:to-primary-500/10 transition-all duration-300 pointer-events-none" />
        </button>
        {renderModal()}
      </>
    );
  }

  return (
    <>
      <button onClick={handleOpen} className={`relative p-2 rounded-xl transition-all ${iconColor} hover:bg-white/10`} aria-label="Notifications">
        <Bell size={iconSize} />
        {totalUnseen > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
            {totalUnseen > 9 ? "9+" : totalUnseen}
          </span>
        )}
      </button>
      {renderModal()}
    </>
  );
}
